import apiClient from './apiClient';
import type {
  PagedResponse,
  EducationSchool,
  EducationClass,
  EducationClassSummary,
  EducationUser,
  EducationUserSummary,
  EmailAuthMethod,
  EducationTeam,
  TeamOperation,
  PrimaryChannel,
  ChannelTab,
  ActivateTeamResult,
  CheckBackupResponse,
  FixGroupsOperationResponse,
  StartFixGroupsRequest
} from '../types';

/**
 * Education Service
 * Handles API calls for Schools, Classes, and Users
 */

export const educationService = {
  // ============================================================================
  // Schools
  // ============================================================================

  async getSchools(
    pageNumber: number = 1,
    pageSize: number = 50,
    searchTerm?: string
  ): Promise<PagedResponse<EducationSchool>> {
    const params = new URLSearchParams();
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());
    if (searchTerm) params.append('searchTerm', searchTerm);

    const response = await apiClient.get<PagedResponse<EducationSchool>>(
      `/EducationSchool/GetAll?${params.toString()}`
    );
    return response.data;
  },

  async getSchoolById(id: string): Promise<EducationSchool> {
    const response = await apiClient.get<EducationSchool>(`/EducationSchool/GetById/${id}`);
    return response.data;
  },

  async getSchoolByExternalId(externalId: string): Promise<EducationSchool> {
    const response = await apiClient.get<EducationSchool>(
      `/EducationSchool/GetByExternalId/${externalId}`
    );
    return response.data;
  },

  // ============================================================================
  // Classes
  // ============================================================================

  async getClasses(
    pageNumber: number = 1,
    pageSize: number = 50,
    searchTerm?: string,
    searchBy?: 'name' | 'classCode' | 'id'
  ): Promise<PagedResponse<EducationClass>> {
    const params = new URLSearchParams();
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());
    if (searchTerm) params.append('searchTerm', searchTerm);
    if (searchBy) params.append('searchBy', searchBy);

    const response = await apiClient.get<PagedResponse<EducationClass>>(
      `/EducationClass/GetAll?${params.toString()}`
    );
    return response.data;
  },

  async getClassesWithNextLink(nextLink: string): Promise<PagedResponse<EducationClass>> {
    const response = await apiClient.post<PagedResponse<EducationClass>>(
      `/EducationClass/GetAllWithNextLink`,
      { nextLink }
    );
    return response.data;
  },

  async getClassesSummary(classIds: string[]): Promise<EducationClassSummary[]> {
    const response = await apiClient.post<EducationClassSummary[]>(
      `/EducationClass/GetClassesSummary`,
      classIds
    );
    return response.data;
  },

  async getClassById(id: string): Promise<EducationClass> {
    const response = await apiClient.get<EducationClass>(`/EducationClass/GetById/${id}`);
    return response.data;
  },

  async getClassByExternalId(externalId: string): Promise<EducationClass> {
    const response = await apiClient.get<EducationClass>(
      `/EducationClass/GetByExternalId/${externalId}`
    );
    return response.data;
  },

  async getClassTeachers(classId: string): Promise<EducationUser[]> {
    const response = await apiClient.get<EducationUser[]>(
      `/EducationClass/${classId}/GetTeachers`
    );
    return response.data;
  },

  async getClassMembers(classId: string): Promise<EducationUser[]> {
    const response = await apiClient.get<EducationUser[]>(
      `/EducationClass/${classId}/GetMembers`
    );
    return response.data;
  },

  async getClassTeam(classId: string): Promise<EducationTeam | null> {
    const response = await apiClient.get<EducationTeam | null>(
      `/EducationClass/${classId}/GetTeam`
    );
    return response.data;
  },

  async getClassTeamOperations(classId: string): Promise<TeamOperation[]> {
    const response = await apiClient.get<TeamOperation[]>(
      `/EducationClass/${classId}/GetTeamOperations`
    );
    return response.data;
  },

  async getClassPrimaryChannel(classId: string): Promise<PrimaryChannel | null> {
    const response = await apiClient.get<PrimaryChannel | null>(
      `/EducationClass/${classId}/GetPrimaryChannel`
    );
    return response.data;
  },

  async getClassPrimaryChannelTabs(classId: string): Promise<ChannelTab[]> {
    const response = await apiClient.get<ChannelTab[]>(
      `/EducationClass/${classId}/GetPrimaryChannelTabs`
    );
    return response.data;
  },

  async getClassSchools(classId: string): Promise<EducationSchool[]> {
    const response = await apiClient.get<EducationSchool[]>(
      `/EducationClass/${classId}/GetSchools`
    );
    return response.data;
  },

  async activateTeam(classId: string): Promise<ActivateTeamResult> {
    const response = await apiClient.post<ActivateTeamResult>(
      `/EducationClass/${classId}/ActivateTeam`
    );
    return response.data;
  },

  async unarchiveTeam(classId: string): Promise<ActivateTeamResult> {
    const response = await apiClient.post<ActivateTeamResult>(
      `/EducationClass/${classId}/UnarchiveTeam`
    );
    return response.data;
  },

  async archiveTeam(classId: string): Promise<ActivateTeamResult> {
    const response = await apiClient.post<ActivateTeamResult>(
      `/EducationClass/${classId}/ArchiveTeam`
    );
    return response.data;
  },

  // ============================================================================
  // Users
  // ============================================================================

  async getUsers(
    pageNumber: number = 1,
    pageSize: number = 50,
    searchTerm?: string,
    userType?: 'student' | 'teacher' | 'staff'
  ): Promise<PagedResponse<EducationUser>> {
    const params = new URLSearchParams();
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());
    if (searchTerm) params.append('searchTerm', searchTerm);
    if (userType) params.append('userType', userType);

    const response = await apiClient.get<PagedResponse<EducationUser>>(
      `/EducationUser/GetAll?${params.toString()}`
    );
    return response.data;
  },

  async getUsersWithNextLink(nextLink: string): Promise<PagedResponse<EducationUser>> {
    const response = await apiClient.post<PagedResponse<EducationUser>>(
      `/EducationUser/GetAllWithNextLink`,
      { nextLink }
    );
    return response.data;
  },

  async getUserById(id: string): Promise<EducationUser> {
    const response = await apiClient.get<EducationUser>(`/EducationUser/GetById/${id}`);
    return response.data;
  },

  async getUserByStudentExternalId(externalId: string): Promise<EducationUser | null> {
    try {
      const response = await apiClient.get<EducationUser>(`/EducationUser/GetByStudentExternalId/${externalId}`);
      return response.data;
    } catch {
      return null;
    }
  },

  async getUserByTeacherExternalId(externalId: string): Promise<EducationUser | null> {
    try {
      const response = await apiClient.get<EducationUser>(`/EducationUser/GetByTeacherExternalId/${externalId}`);
      return response.data;
    } catch {
      return null;
    }
  },

  async getUserByStaffExternalId(externalId: string): Promise<EducationUser | null> {
    try {
      const response = await apiClient.get<EducationUser>(`/EducationUser/GetByStaffExternalId/${externalId}`);
      return response.data;
    } catch {
      return null;
    }
  },

  async getUserClasses(userId: string): Promise<EducationClass[]> {
    const response = await apiClient.get<EducationClass[]>(
      `/EducationUser/${userId}/GetClasses`
    );
    return response.data;
  },

  async getUserSchools(userId: string): Promise<EducationSchool[]> {
    const response = await apiClient.get<EducationSchool[]>(
      `/EducationUser/${userId}/GetSchools`
    );
    return response.data;
  },

  async getUserEmailAuthMethod(userId: string): Promise<EmailAuthMethod> {
    const response = await apiClient.get<EmailAuthMethod>(
      `/EducationUser/${userId}/GetEmailAuthMethod`
    );
    return response.data;
  },

  async getUserOwnedClasses(userId: string): Promise<EducationClass[]> {
    const response = await apiClient.get<EducationClass[]>(
      `/EducationUser/${userId}/GetOwnedClasses`
    );
    return response.data;
  },

  async getUsersSummary(userIds: string[]): Promise<EducationUserSummary[]> {
    const response = await apiClient.post<EducationUserSummary[]>(
      `/EducationUser/GetUsersSummary`,
      userIds
    );
    return response.data;
  },

  // ============================================================================
  // Fix Groups (Correcao de Grupos de Usuarios)
  // ============================================================================

  /**
   * Verifica se existe backup de grupos para o usuario
   */
  async checkFixGroupsBackup(userId: string): Promise<CheckBackupResponse> {
    const response = await apiClient.get<CheckBackupResponse>(
      `/FixGroups/CheckBackup/${userId}`
    );
    return response.data;
  },

  /**
   * Inicia uma operacao de correcao de grupos
   */
  async startFixGroups(
    userId: string,
    userDisplayName: string,
    useBackup: boolean
  ): Promise<FixGroupsOperationResponse> {
    const request: StartFixGroupsRequest = {
      userId,
      userDisplayName,
      useBackup,
    };
    const response = await apiClient.post<FixGroupsOperationResponse>(
      `/FixGroups/Start`,
      request
    );
    return response.data;
  },

  /**
   * Obtem o status de uma operacao de correcao de grupos
   */
  async getFixGroupsOperation(operationId: string): Promise<FixGroupsOperationResponse> {
    const response = await apiClient.get<FixGroupsOperationResponse>(
      `/FixGroups/${operationId}`
    );
    return response.data;
  },
};

export default educationService;
